import { CircularProgress, IconButton, Skeleton, TableCell, TableRow, Tooltip } from '@mui/material';
import { useClientWorkstreamData } from '../../hooks/useClientWorkstreamData';
import { useStyles } from './SubProjectTable.styles';
import { ClientWorkstreamData } from '@modules/EvaluationModule/interfaces/ClientWorkstreamData';
import { DataState } from '@shared/enums/DataState';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import StarIcon from '@mui/icons-material/Star';
import CopyLinkIcon from '@mui/icons-material/Link';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CreateIcon from '@mui/icons-material/Create';
import classNames from 'classnames';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { copyToClipBoard } from '@shared/helpers/copyToClipBoard';
import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { displayNotification } from '@modules/App/redux/notifications/actions';
import { ClientRmContactStatus } from '@modules/EvaluationModule/enums/ClientRmContactStatus';
import { ManageClientModal } from '../ManageClientModal/ManageClientModal';
import { ClientProjectData } from '@modules/EvaluationModule/interfaces/ClientProjectData';
import { getGoogleCalendarMeetingLink } from '@modules/HappinessModule/helpers/googleCalendar';
import { useAuth } from '@shared/hooks/useAuth';
import { WorkstreamStatus, FeedbackGiver } from '../../enums/ContactFilterOptions';
import { clientSubProjectFilterSelector } from '../../redux/selectors';
import { ClientContactData } from '@modules/EvaluationModule/interfaces/ClientSubProjectData';

interface Props {
  workstream: ClientWorkstreamData;
  choosenProject: ClientProjectData;
  workstreams: ClientWorkstreamData[];
}
export const SubProjectTableRow = ({ workstream, choosenProject, workstreams }: Props) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const noName = 'No name';
  const clientSubProjectFilter = useSelector(clientSubProjectFilterSelector);

  const rowRef = useRef<HTMLTableRowElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasTriggered = useRef(false);

  // Contacts load only once the row scrolls into view (100px early), then stay cached.
  useEffect(() => {
    if (!rowRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(rowRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // The pseudo row never fetches by itself: its contacts arrive with the per-client Zoho check.
  const wsContacts = useClientWorkstreamData({
    workstreamId: workstream.zoho_crm_id,
    clientId: choosenProject.id,
    enabled: isVisible && workstream.isWorkstream,
  });

  // Stale-while-revalidate: same rule as SubProjectTable — a TTL-expired background refetch
  // (Pending, with the previous contacts preserved) keeps the existing rows on screen.
  const isLoading = isVisible && wsContacts.state === DataState.Pending && !wsContacts.data;

  const filteredContacts = useMemo(() => {
    if (!wsContacts.data || wsContacts.data.length === 0) return [];
    const filters = clientSubProjectFilter;
    return wsContacts.data.filter((contact) => {
      return (
        (filters.typeOfClient.length === 0 || filters.typeOfClient.includes(contact.rmTypeOfContact)) &&
        (filters.clientTitle.length === 0 || filters.clientTitle.includes(contact.title)) &&
        (filters.status.length === 0 ||
          (contact.rmContactStatus && filters.status.includes(contact.rmContactStatus))) &&
        // ROCKY-27762: hide former POCs/employees unless the manager asks for them.
        // Case-insensitive "former", not a `Former` prefix: the backend enum
        // (models.py:229) also carries `Main former POC`, which a prefix test misses.
        // A contact with no status stays visible.
        (filters.showFormerContacts || !/former/i.test(contact.rmContactStatus ?? '')) &&
        (filters.contactOwner.length === 0 ||
          (contact.contactOwner && filters.contactOwner.includes(contact.contactOwner))) &&
        (filters.contactLocation.length === 0 || filters.contactLocation.includes(contact.location)) &&
        (filters.locationArea.length === 0 || filters.locationArea.includes(contact.locationArea)) &&
        satisfiesFeedbackFilter(contact, filters.feedbackGiver as string[])
      );
    });
  }, [wsContacts.data, clientSubProjectFilter]);

  const handleCopyLink = useCallback((e, email) => {
    copyToClipBoard(e)(email, () => {
      dispatch(displayNotification("Contact's email was copied to clipboard"));
    });
  }, []);

  const scheduleMeeting = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, email: string, name: string) => {
      e.stopPropagation();
      window.open(
        getGoogleCalendarMeetingLink({
          inviteeName: currentUser?.name || '',
          inviteeEmail: email.toLowerCase(),
          inviterName: name,
        })
      );
    },
    []
  );

  const sendMail = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, email: string) => {
    e.stopPropagation();
    window.open(`mailto:${email.toLowerCase()}`);
  }, []);

  return (
    <>
      <TableRow className="no-height" ref={rowRef}>
        <TableCell colSpan={6} className={classNames(styles.backgroundNeutral, styles.stickyWorkstreamTitle)}>
          <span className={styles.subProjectName}>
            {workstream.status === WorkstreamStatus.Closed ? (
              <WorkOffOutlinedIcon />
            ) : filteredContacts.length ? (
              <FolderSharedOutlinedIcon />
            ) : (
              <FolderOutlinedIcon />
            )}

            {workstream.name}
            {isLoading && <CircularProgress size={14} sx={{ ml: 1 }} />}
            {wsContacts.state === DataState.Fulfilled && filteredContacts.length === 0 && (
              <span className={styles.noContactText}> No contacts</span>
            )}
          </span>
        </TableCell>
      </TableRow>

      {!workstream?.zoho_crm_id && (
        <TableCell colSpan={6}>
          <div className={classNames(styles.emptyClients, styles.nothingInZoho)}>
            There is no information associated with {workstream.name}.<br />
            Please contact Zoho CRM admins.
          </div>
        </TableCell>
      )}

      {workstream?.zoho_crm_id && isLoading && (
        <TableRow aria-busy="true" aria-label="Loading contacts">
          <TableCell>
            <Skeleton variant="text" width="80%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="70%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="50%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" />
          </TableCell>
        </TableRow>
      )}

      {workstream?.zoho_crm_id && wsContacts.state === DataState.Rejected && (
        <TableRow>
          <TableCell colSpan={6}>
            <span className={styles.noContactText}>Failed to fetch contacts</span>
          </TableCell>
        </TableRow>
      )}

      {workstream?.zoho_crm_id &&
        filteredContacts.map((contact) => {
          const contactName: string =
            contact.firstName === noName && contact.lastName === noName
              ? noName
              : contact.firstName === noName && contact.lastName !== noName
              ? contact.lastName
              : contact.firstName !== noName && contact.lastName === noName
              ? contact.firstName
              : `${contact.firstName} ${contact.lastName}`;
          return (
            <ManageClientModal
              contact={contact}
              choosenProject={choosenProject}
              workstream={workstream}
              key={contact.id}
              workstreams={workstreams}
            >
              <TableCell>
                <p className={styles.personName} id="name">
                  {contactName}
                </p>
                <p className={classNames(styles.subTitle, `${!contact.emailAeTool && styles.errorText}`)}>
                  {contact.emailAeTool.toLowerCase() || 'No email available'}
                  {contact.emailAeTool && (
                    <IconButton onClick={(e) => handleCopyLink(e, contact.emailAeTool)}>
                      <ContentCopyRoundedIcon />
                    </IconButton>
                  )}
                </p>
              </TableCell>
              <TableCell>{contact.title}</TableCell>
              <TableCell>
                <span
                  className={classNames(
                    styles.contactStatusText,
                    `${contact.rmContactStatus === ClientRmContactStatus.formerPoc && styles.noContactText}`
                  )}
                >
                  {!contact.rmContactStatus && <HorizontalRuleIcon className={styles.noContactText} />}
                  {contact.rmContactStatus === ClientRmContactStatus.mainCurrentPoc && <StarIcon />}
                  {contact.rmContactStatus && contact.rmContactStatus}
                </span>
              </TableCell>

              <TableCell className={styles.contactLocation}>{contact.location}</TableCell>

              <TableCell>
                <div className={styles.subProjectName}>
                  {contact.feedbackGiver ? (
                    <Tooltip
                      title={contact.doNotTouch ? 'Sending link manually' : 'Eval feedback giver'}
                      placement="top"
                    >
                      <div className={styles.emailStatusContainer}>
                        {contact.doNotTouch ? <CopyLinkIcon /> : <WorkspacePremiumOutlinedIcon />}
                      </div>
                    </Tooltip>
                  ) : (
                    <div className={classNames(styles.emailStatusContainer, styles.disabledTextLight)}>
                      <WorkspacePremiumOutlinedIcon />
                    </div>
                  )}

                  {contact.csatGiver ? (
                    <Tooltip title="CSAT giver" placement="top">
                      <div className={styles.emailStatusContainer}>
                        <ContactMailOutlinedIcon />
                      </div>
                    </Tooltip>
                  ) : (
                    <div className={classNames(styles.emailStatusContainer, styles.disabledTextLight)}>
                      <ContactMailOutlinedIcon />
                    </div>
                  )}
                </div>
              </TableCell>

              <TableCell>
                <span className={styles.subProjectName}>
                  <Tooltip title="Schedule a call" placement="top">
                    <IconButton
                      className={styles.scheduleButton}
                      onClick={(e) => scheduleMeeting(e, contact.emailAeTool, contactName)}
                    >
                      <VideoCallOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Send email" placement="top">
                    <IconButton
                      className={styles.scheduleButton}
                      onClick={(e) => sendMail(e, contact.emailAeTool.toLowerCase())}
                    >
                      <EmailOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit contact" placement="top">
                    <IconButton className={styles.editContactButton}>
                      <CreateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>
              </TableCell>
            </ManageClientModal>
          );
        })}
    </>
  );
};

function satisfiesFeedbackFilter(contact: ClientContactData, feedbackFilter: string[]): boolean {
  return (
    feedbackFilter.length === 0 ||
    feedbackFilter.every((filter) => {
      switch (filter) {
        case FeedbackGiver.CsatGiver:
          return contact.csatGiver;
        case FeedbackGiver.FeedbackGiver:
          return contact.feedbackGiver;
        case FeedbackGiver.NotCsatGiver:
          return !contact.csatGiver;
        case FeedbackGiver.NotFeedbackGiver:
          return !contact.feedbackGiver;
        default:
          return true;
      }
    })
  );
}
